import { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ajaxService from "../../../services/ajax-service";
import {
  Button,
  Table,
  Typography,
  notification,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Tag,
  Row,
  Col,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  CalendarOutlined,
  SearchOutlined,
  PlusOutlined,
  LinkOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  calculateTableScrollX,
  calculateModalWidth,
} from "../../../utils/widthCalculator";
import { useTableHover } from "../../../hooks/useTableHover";

dayjs.extend(customParseFormat);

const { Text, Title } = Typography;
const { TextArea } = Input;

const PAGE_LIMIT = 15;

const buildEventsQuery = ({ name = "", page = 1, limit = PAGE_LIMIT, sort = "date_asc", status = "" }) => {
  const params = new URLSearchParams();
  if (name) params.append("name", name);
  params.append("page", String(page));
  params.append("limit", String(limit));
  if (sort) params.append("sort", sort);
  if (status) params.append("status", status);
  return `/admin/events?${params.toString()}`;
};

const parseEventListResponse = (data, page = 1) => ({
  data: Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [],
  current_page: data?.current_page || data?.meta?.current_page || page,
  per_page: data?.per_page || data?.meta?.per_page || PAGE_LIMIT,
  total: data?.total || data?.meta?.total || 0,
  meta: data?.meta || {
    current_page: data?.current_page || page,
    per_page: data?.per_page || PAGE_LIMIT,
    total: data?.total || 0,
  },
});

const parseEventDateTime = (date, time) => {
  if (time) {
    const direct = dayjs(time);
    if (direct.isValid() && /^\d{4}-\d{2}-\d{2}/.test(String(time))) {
      return direct;
    }
  }
  if (!date) return null;
  const timePart = time || "00:00";
  const parsed = dayjs(`${date} ${timePart}`, [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD H:mm",
  ], true);
  return parsed.isValid() ? parsed : dayjs(date);
};

const showApiErrors = (response) => {
  const errors = response?.data?.errors || response?.data;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    const messages = Object.values(errors).flat();
    if (messages.length > 0) {
      messages.forEach((msg) => {
        notification.open({
          message: String(msg),
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      });
      return;
    }
  }
  notification.open({
    message: response?.message || "Operation failed",
    icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
  });
};

const Events = () => {
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  
  // Use custom table hover hook to prevent fixed column overlap
  useTableHover();

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState("50%");

  const [filter, setFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [sortOrder, setSortOrder] = useState("date_asc");
  const [statusFilter, setStatusFilter] = useState("");
  const debounceTimer = useRef(null);
  const [address, setAddress] = useState("");
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const geocodeTimerRef = useRef(null);
  const lastGeocodedAddress = useRef("");

  const [dataSource, setDataSource] = useState({
    data: [],
    current_page: 1,
    per_page: PAGE_LIMIT,
    total: 0,
    meta: { current_page: 1, per_page: PAGE_LIMIT, total: 0 },
  });

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 70,
      fixed: "left",
      render: (text, record, index) => {
        const currentPage =
          dataSource.meta?.current_page || dataSource.current_page || 1;
        const perPage = dataSource.meta?.per_page || dataSource.per_page || PAGE_LIMIT;
        const serialNumber = (currentPage - 1) * perPage + index + 1;
        return <Text style={{ fontSize: "0.85rem" }}>{serialNumber}</Text>;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      ellipsis: true,
      render: (name) => <Text style={{ fontSize: "0.85rem" }}>{name}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date, record) => {
        const displayDate =
          date ||
          (record.start_time && dayjs(record.start_time).isValid()
            ? dayjs(record.start_time).format("YYYY-MM-DD")
            : null);
        return (
          <Text style={{ fontSize: "0.85rem" }}>
            {displayDate ? dayjs(displayDate).format("DD-MMM-YYYY") : "N/A"}
          </Text>
        );
      },
    },
    {
      title: "Going",
      dataIndex: "going_display",
      key: "going_display",
      width: 150,
      render: (goingDisplay, record) => (
        <Text style={{ fontSize: "0.85rem" }}>
          {goingDisplay || `${record.going_count ?? 0} Going`}
        </Text>
      ),
    },
    {
      title: "Capacity",
      dataIndex: "capacity_display",
      key: "capacity_display",
      width: 100,
      render: (capacityDisplay) => (
        <Text style={{ fontSize: "0.85rem" }}>{capacityDisplay || "Unlimited"}</Text>
      ),
    },
    {
      title: "Remaining",
      dataIndex: "spots_remaining_display",
      key: "spots_remaining_display",
      width: 100,
      render: (spotsDisplay) => (
        <Text style={{ fontSize: "0.85rem" }}>{spotsDisplay ?? "—"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status, record) => (
        <Tag color={record.is_full ? "red" : "green"}>{status || "Open"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (text, record) => (
        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
          <Button
            type="text"
            size="small"
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              color: "var(--primary-purple)",
              border: "1px solid var(--primary-purple)",
              borderRadius: "var(--radius-md)",
              transition: "var(--transition-fast)",
            }}
            icon={<EyeOutlined style={{ fontSize: "12px" }} />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              color: "var(--accent-purple)",
              border: "1px solid var(--accent-purple)",
              borderRadius: "var(--radius-md)",
              transition: "var(--transition-fast)",
            }}
            icon={<EditOutlined style={{ fontSize: "12px" }} />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              color: "var(--error-red)",
              border: "1px solid var(--error-red)",
              borderRadius: "var(--radius-md)",
              transition: "var(--transition-fast)",
            }}
            icon={<DeleteOutlined style={{ fontSize: "12px" }} />}
            onClick={() => handleDeleteClick(record.id)}
          />
        </div>
      ),
    },
  ];

  const handleDeleteClick = (id) => {
    setEventToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (eventToDelete) {
      try {
        const response = await ajaxService.delete("/admin/events/", eventToDelete);
        if (response.success) {
          notification.open({
            message: "Event deleted successfully!",
            icon: (
              <CheckCircleOutlined style={{ color: "var(--success-green)" }} />
            ),
          });
          fetchEvents(dataSource.meta?.current_page || dataSource.current_page || 1);
        } else {
          notification.open({
            message: "Failed to delete event.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        notification.open({
          message: "Failed to delete event.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
    setDeleteModalVisible(false);
    setEventToDelete(null);
  };

  // Geocoding function - tries Google first, then falls back to free service
  const geocodeAddress = async (addressText) => {
    if (!addressText || addressText.trim() === "") {
      return null;
    }

    setGeocodingLoading(true);

    try {
      // Try Google Geocoding API first if key is available
      if (googleApiKey) {
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              addressText
            )}&key=${googleApiKey}`
          );
          const data = await response.json();
          if (data.status === "OK" && data.results && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            setGeocodingLoading(false);
            return {
              lat: location.lat.toString(),
              lng: location.lng.toString(),
            };
          }
        } catch (error) {
          console.log("Google Geocoding failed, trying fallback:", error);
        }
      }

      // Fallback to OpenStreetMap Nominatim (free service)
      // Add a small delay to respect rate limits (1 request per second)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          addressText
        )}&limit=1`,
        {
          headers: {
            "User-Agent": "Tandemly-Web-App", // Required by Nominatim
          },
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setGeocodingLoading(false);
        return {
          lat: data[0].lat,
          lng: data[0].lon,
        };
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
      setGeocodingLoading(false);
      notification.warning({
        message: "Could not get location coordinates",
        description: "Please enter latitude and longitude manually.",
      });
    }

    setGeocodingLoading(false);
    return null;
  };

  // // Load Google Maps script
  // useEffect(() => {
  //   const checkGoogleApiKey = async () => {
  //     try {
  //       // Try to get Google API key from backend or environment
  //       const response = await ajaxService.get("/get-google-api-key");
  //       if (response.success && response.data?.api_key) {
  //         setGoogleApiKey(response.data.api_key);
  //         loadGoogleMapsScript(response.data.api_key);
  //       } else {
  //         // Check environment variable or config
  //         const envApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  //         if (envApiKey) {
  //           setGoogleApiKey(envApiKey);
  //           loadGoogleMapsScript(envApiKey);
  //         } else {
  //           // No API key, will use free service
  //           setGoogleApiKey(null);
  //         }
  //       }
  //     } catch (error) {
  //       // Check environment variable as fallback
  //       const envApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  //       if (envApiKey) {
  //         setGoogleApiKey(envApiKey);
  //         loadGoogleMapsScript(envApiKey);
  //       } else {
  //         // If endpoint doesn't exist, use free service
  //         setGoogleApiKey(null);
  //       }
  //     }
  //   };

  //   checkGoogleApiKey();
  // }, []);

  const loadGoogleMapsScript = (apiKey) => {
    if (window.google && window.google.maps) {
      setGoogleScriptLoaded(true);
      return;
    }

    // Check if script already exists
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      return;
    }

    const script = document.createElement("script");
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleScriptLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps script");
      setGoogleScriptLoaded(false);
    };
    document.head.appendChild(script);
  };

  // Ref to track autocomplete init timer
  const autocompleteTimerRef = useRef(null);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const isViewMode = editingEvent?.isViewMode === true;
    // Only initialize autocomplete if not in view mode
    if (
      showModal &&
      !isViewMode &&
      googleScriptLoaded &&
      window.google &&
      window.google.maps &&
      window.google.maps.places
    ) {
      // Small delay to ensure DOM is ready
      autocompleteTimerRef.current = setTimeout(() => {
        const addressInput = document.getElementById("event-address-input");
        if (addressInput) {
          // Clean up existing autocomplete if any
          if (autocompleteRef.current) {
            // Remove existing listener
            window.google.maps.event.clearInstanceListeners(
              autocompleteRef.current
            );
            autocompleteRef.current = null;
          }

          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            addressInput,
            {
              types: ["address"],
              fields: ["formatted_address", "geometry", "name"],
            }
          );

          autocompleteRef.current.addListener("place_changed", async () => {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              const formattedAddress = place.formatted_address || place.name;
              form.setFieldsValue({
                address: formattedAddress,
                lat_location: lat.toString(),
                long_location: lng.toString(),
              });
              setAddress(formattedAddress);
              // Update last geocoded address to prevent duplicate geocoding
              lastGeocodedAddress.current = formattedAddress;
              notification.success({
                message: "Address selected",
                description: "Location coordinates have been auto-filled.",
                duration: 2,
              });
            }
          });
        }
      }, 100);
    }

    // Cleanup on modal close or view mode
    return () => {
      // Clear the initialization timer
      if (autocompleteTimerRef.current) {
        clearTimeout(autocompleteTimerRef.current);
        autocompleteTimerRef.current = null;
      }
      if ((!showModal || isViewMode) && autocompleteRef.current) {
        if (window.google && window.google.maps && window.google.maps.event) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
        }
        autocompleteRef.current = null;
      }
    };
  }, [googleScriptLoaded, showModal, editingEvent?.isViewMode]);

  const populateFormFromEvent = (event) => {
    const eventAddress = event.address || "";
    setAddress(eventAddress);
    lastGeocodedAddress.current = eventAddress;
    form.setFieldsValue({
      name: event.name,
      description: event.description,
      address: eventAddress,
      lat_location: event.lat_location,
      long_location: event.long_location,
      max_capacity: event.max_capacity ?? "",
      start_time: parseEventDateTime(event.date, event.start_time),
      end_time: parseEventDateTime(event.date, event.end_time),
    });
  };

  const handleView = (event) => {
    history.push(`/events/${event.id}`);
  };

  const handleEdit = (event) => {
    setEditingEvent({ ...event, isViewMode: false });
    setShowModal(true);
    populateFormFromEvent(event);
  };

  const openEditById = async (eventId) => {
    const response = await ajaxService.get(`/admin/events/${eventId}`);
    if (response?.success) {
      handleEdit(response.data);
    } else {
      notification.open({
        message: response?.message || "Failed to load event",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingEvent(null);
    setAddress("");
    lastGeocodedAddress.current = "";
    form.resetFields();

    // Clear geocode timer
    if (geocodeTimerRef.current) {
      clearTimeout(geocodeTimerRef.current);
      geocodeTimerRef.current = null;
    }

    // Clean up autocomplete
    if (autocompleteRef.current) {
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
      autocompleteRef.current = null;
    }
  };

  const buildEventPayload = (values, isEdit) => {
    const payload = { name: values.name };

    if (values.description) payload.description = values.description;
    if (values.address) payload.address = values.address;
    if (values.lat_location) payload.lat_location = values.lat_location;
    if (values.long_location) payload.long_location = values.long_location;

    if (values.start_time) {
      payload.date = values.start_time.format("YYYY-MM-DD");
      payload.start_time = values.start_time.format("YYYY-MM-DD HH:mm:ss");
    }
    if (values.end_time) {
      payload.end_time = values.end_time.format("YYYY-MM-DD HH:mm:ss");
    }

    const capacity = values.max_capacity;
    if (capacity !== undefined && capacity !== null && capacity !== "") {
      payload.max_capacity = parseInt(capacity, 10);
    } else if (isEdit) {
      payload.max_capacity = null;
    }

    return payload;
  };

  const onFinish = async (values) => {
    setLoading(true);
    const isEdit = Boolean(editingEvent?.id);
    const payload = buildEventPayload(values, isEdit);

    try {
      let response;
      if (isEdit) {
        response = await ajaxService.put("/admin/events/", editingEvent.id, payload);
      } else {
        response = await ajaxService.post("/admin/events", payload);
      }

      if (response?.success) {
        handleCancel();
        fetchEvents(1);
        notification.open({
          message: isEdit ? "Event updated successfully!" : "Event created successfully!",
          icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
        });
      } else {
        showApiErrors(response);
      }
    } catch (error) {
      console.error("Error:", error);
      notification.open({
        message: "An error occurred. Please try again.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = useCallback(
    async (page = 1, searchName = filter) => {
      const query = buildEventsQuery({
        name: searchName,
        page,
        limit: PAGE_LIMIT,
        sort: sortOrder,
        status: statusFilter,
      });
      const response = await ajaxService.get(query);
      if (response?.success) {
        setDataSource(parseEventListResponse(response.data, page));
      }
    },
    [filter, sortOrder, statusFilter]
  );

  const handleSearch = async ({ name }) => {
    const searchName = name ? name.trim() : "";
    const query = buildEventsQuery({
      name: searchName,
      page: 1,
      limit: PAGE_LIMIT,
      sort: sortOrder,
      status: statusFilter,
    });
    const response = await ajaxService.get(query);
    if (response?.success) {
      const parsed = parseEventListResponse(response.data, 1);
      if (parsed.data.length === 0 && searchName) {
        notification.open({
          message: `${searchName} is not found!`,
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      } else {
        setFilter(searchName);
      }
      setDataSource(parsed);
    }
  };

  // Debounced search handler
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchValue !== undefined) {
      debounceTimer.current = setTimeout(() => {
        handleSearch({ name: searchValue });
      }, 500);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue]);

  const handleChangePage = async (page) => {
    await fetchEvents(page);
  };

  useEffect(() => {
    fetchEvents(1);
  }, [sortOrder, statusFilter]);

  useEffect(() => {
    const editEventId = location.state?.editEventId;
    if (editEventId) {
      openEditById(editEventId);
      history.replace("/events");
    }
  }, [location.state]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener("sidebarCollapse", handleSidebarCollapse);
    return () =>
      window.removeEventListener("sidebarCollapse", handleSidebarCollapse);
  }, []);

  // Calculate table scroll width
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 1200, 1600, 40);
  }, [sidebarCollapsed]);

  const [tableWidth, setTableWidth] = useState(() =>
    calculateTableScrollX(false, 1200, 1600, 40)
  );

  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth());
      setModalWidth(calculateModalWidth("50%"));
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [calculateTableWidth]);

  const renderModal = (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={showModal}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <div
        style={{
          padding: "var(--spacing-2xl) var(--spacing-xl)",
          background: "var(--dark-bg-card)",
          borderRadius: "var(--radius-lg)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            marginBottom: "var(--spacing-2xl)",
            textAlign: "center",
            paddingBottom: "var(--spacing-lg)",
            borderBottom: "1px solid rgba(254, 2, 125, 0.2)",
          }}
        >
          <Title
            level={3}
            style={{
              color: "var(--text-white)",
              margin: 0,
              fontFamily: "Poppins-Bold",
              fontSize: "1.5rem",
            }}
          >
            {editingEvent?.id ? "Edit Event" : "Create New Event"}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="event-form"
        >
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                label={
                  <span
                    style={{
                      color: "var(--text-white)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      display: "block",
                      marginBottom: "2px",
                    }}
                  >
                    Event Name
                  </span>
                }
                name="name"
                rules={[{ required: true, message: "Event name is required" }]}
              >
                <Input
                  placeholder="Enter event name"
                  allowClear
                  className="input-field"
                  size="large"
                  style={{ border: "1px solid rgba(254, 2, 125, 0.3)" }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <span style={{ color: "var(--text-white)", fontWeight: 600, fontSize: "0.95rem" }}>
                    Description
                  </span>
                }
                name="description"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter event description"
                  allowClear
                  className="input-field"
                  style={{ border: "1px solid rgba(254, 2, 125, 0.3)" }}
                />
              </Form.Item>
            </Col>

            {/* <Col span={12}>
              <Form.Item
                label={
                  <span
                    style={{
                      color: "var(--text-white)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      display: "block",
                      marginBottom: "2px",
                    }}
                  >
                    Start Date
                  </span>
                }
                name="start_date"
                rules={[{ required: true, message: "Date is required" }]}
              >
                <DatePicker
                  style={{
                    width: "100%",
                    border: "1px solid rgba(254, 2, 125, 0.3)",
                  }}
                  allowClear
                  className="input-field"
                  size="large"
                  format="YYYY-MM-DD"
                  disabled={editingEvent?.isViewMode === true}
                />
              </Form.Item>
            </Col> */}

            {/* <Col span={12}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: '2px'
                }}>Code</span>}
                name="code"
            >
                <Input 
                  placeholder="Enter event code" 
                allowClear
                className="input-field"
                size="large"
                style={{ border: '1px solid rgba(254, 2, 125, 0.3)' }}
                  disabled={editingEvent?.isViewMode === true}
                />
            </Form.Item>
          </Col> */}

            <Col span={12}>
              <Form.Item
                label={
                  <span
                    style={{
                      color: "var(--text-white)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      display: "block",
                      marginBottom: "var(--spacing-xs)",
                    }}
                  >
                    Start Date & Time
                  </span>
                }
                name="start_time"
                extra={
                  <span style={{ color: "var(--text-gray-light)", fontSize: "0.8rem" }}>
                    Times are in UTC.
                  </span>
                }
                rules={[{ required: true, message: "Start date and time is required" }]}
              >
                <DatePicker
                  showTime={{
                    format: 'h:mm A',
                    use12Hours: true,
                    hideDisabledOptions: true,
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid rgba(254, 2, 125, 0.3)",
                  }}
                  allowClear
                  className="input-field"
                  size="large"
                  format="YYYY-MM-DD h:mm A"
                  disabledDate={(current) => {
                    // Disable all dates before today
                    return current && current.isBefore(dayjs().startOf('day'));
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <span
                    style={{
                      color: "var(--text-white)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      display: "block",
                      marginBottom: "var(--spacing-xs)",
                    }}
                  >
                    End Date & Time
                  </span>
                }
                name="end_time"
                extra={
                  <span style={{ color: "var(--text-gray-light)", fontSize: "0.8rem" }}>
                    Times are in UTC.
                  </span>
                }
                rules={[
                  { required: true, message: "End date and time is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startTime = getFieldValue('start_time');
                      if (!value || !startTime) {
                        return Promise.resolve();
                      }
                      if (value.isBefore(startTime) || value.isSame(startTime)) {
                        return Promise.reject(new Error('End date and time must be after start date and time'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  showTime={{
                    format: 'h:mm A',
                    use12Hours: true,
                    hideDisabledOptions: true,
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid rgba(254, 2, 125, 0.3)",
                  }}
                  allowClear
                  className="input-field"
                  size="large"
                  format="YYYY-MM-DD h:mm A"
                  disabledDate={(current) => {
                    const startTime = form.getFieldValue('start_time');
                    // Disable dates before today
                    if (current && current.isBefore(dayjs().startOf('day'))) {
                      return true;
                    }
                    // Disable dates before start date if start date is selected
                    if (startTime && current && current.isBefore(dayjs(startTime).startOf('day'))) {
                      return true;
                    }
                    return false;
                  }}
                  disabledTime={(current) => {
                    const startTime = form.getFieldValue('start_time');
                    if (!startTime || !current) {
                      return {};
                    }
                    
                    // Only disable times if the selected date is the same as start date
                    if (current.format('YYYY-MM-DD') === dayjs(startTime).format('YYYY-MM-DD')) {
                      const startHour = dayjs(startTime).hour();
                      const startMinute = dayjs(startTime).minute();
                      
                      return {
                        disabledHours: () => {
                          const hours = [];
                          for (let i = 0; i < startHour; i++) {
                            hours.push(i);
                          }
                          return hours;
                        },
                        disabledMinutes: (selectedHour) => {
                          if (selectedHour === startHour) {
                            const minutes = [];
                            for (let i = 0; i <= startMinute; i++) {
                              minutes.push(i);
                            }
                            return minutes;
                          }
                          return [];
                        },
                      };
                    }
                    
                    return {};
                  }}
                />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
            <Form.Item
              label={<span style={{ 
                color: 'var(--text-white)', 
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 'var(--spacing-xs)'
                }}>Address</span>}
                name="address"
                rules={[{ required: true, message: 'Address is required' }]}
            >
              <Input 
                id="event-address-input"
                placeholder="Enter full address (will auto-fill latitude and longitude)" 
                allowClear 
                className="input-field"
                size="large"
                style={{ border: '1px solid rgba(254, 2, 125, 0.3)' }}
                value={address}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setAddress(newValue);
                  form.setFieldsValue({ address: newValue });
                  
                  // Debounced geocoding when address changes (for edit mode)
                  const isViewMode = editingEvent?.isViewMode === true;
                  if (!isViewMode && newValue.trim() !== '' && newValue !== lastGeocodedAddress.current) {
                    // Clear previous timer
                    if (geocodeTimerRef.current) {
                      clearTimeout(geocodeTimerRef.current);
                    }
                    
                    // Set new timer for debounced geocoding (2 seconds after user stops typing)
                    geocodeTimerRef.current = setTimeout(async () => {
                      // Only geocode if not from autocomplete
                      if (!autocompleteRef.current || !autocompleteRef.current.getPlace()) {
                        const coordinates = await geocodeAddress(newValue);
                        if (coordinates) {
                          form.setFieldsValue({
                            lat_location: coordinates.lat,
                            long_location: coordinates.lng,
                          });
                          lastGeocodedAddress.current = newValue;
                          notification.success({
                            message: 'Location coordinates updated',
                            description: 'Latitude and longitude have been auto-filled from the address.',
                            duration: 2,
                          });
                        }
                      }
                    }, 2000);
                  }
                }}
                onBlur={async (e) => {
                  const addressValue = e.target.value;
                  const isViewMode = editingEvent?.isViewMode === true;
                  
                  // Clear any pending geocode timer
                  if (geocodeTimerRef.current) {
                    clearTimeout(geocodeTimerRef.current);
                    geocodeTimerRef.current = null;
                  }
                  
                  // Geocode immediately on blur if address changed and not in view mode
                  if (addressValue && addressValue.trim() !== '' && !isViewMode) {
                    // Only geocode if address is different from last geocoded or coordinates are missing
                    const currentLat = form.getFieldValue('lat_location');
                    const currentLng = form.getFieldValue('long_location');
                    const needsGeocoding = addressValue !== lastGeocodedAddress.current || !currentLat || !currentLng;
                    
                    if (needsGeocoding) {
                      // Only geocode if not from autocomplete (autocomplete handles its own geocoding)
                      if (!autocompleteRef.current || !autocompleteRef.current.getPlace()) {
                        const coordinates = await geocodeAddress(addressValue);
                        if (coordinates) {
                          form.setFieldsValue({
                            lat_location: coordinates.lat,
                            long_location: coordinates.lng,
                          });
                          lastGeocodedAddress.current = addressValue;
                          notification.success({
                            message: 'Location coordinates updated',
                            description: 'Latitude and longitude have been auto-filled from the address.',
                            duration: 2,
                          });
                        }
                      }
                    }
                  }
                }}
                disabled={editingEvent?.isViewMode === true}
                suffix={
                  geocodingLoading ? (
                    <span style={{ color: 'var(--text-gray-light)', fontSize: '0.75rem' }}>Loading...</span>
                  ) : (
                    <EnvironmentOutlined style={{ color: 'var(--text-gray-light)' }} />
                  )
                }
              />
            </Form.Item>
          </Col> */}

            <Col span={24}>
              <Form.Item
                label={
                  <span
                    style={{
                      color: "var(--text-white)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      display: "block",
                      marginBottom: "var(--spacing-xs)",
                      
                    }}
                  >
                    Address
                  </span>
                }
                name="address"
                rules={[{ required: true, message: "Address is required" }]}
              >
                <Input
                  placeholder="Enter full address"
                  allowClear
                  className="input-field"
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ color: "var(--text-white)", fontWeight: 600, fontSize: "0.95rem" }}>
                    Maximum Capacity
                  </span>
                }
                name="max_capacity"
                extra={
                  <span style={{ color: "var(--text-gray-light)", fontSize: "0.8rem" }}>
                    Leave blank for unlimited attendance.
                  </span>
                }
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === "") {
                        return Promise.resolve();
                      }
                      const num = Number(value);
                      if (!Number.isInteger(num) || num < 1) {
                        return Promise.reject(new Error("Enter a whole number ≥ 1"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input type="number" min={1} step={1} placeholder="Unlimited" allowClear className="input-field" size="large" />
              </Form.Item>
            </Col>

            {/* 
          <Col span={12}>
            <Form.Item
              label={<span style={{ 
                color: 'var(--text-white)', 
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 'var(--spacing-xs)'
                }}>Latitude</span>}
                name="lat_location"
            >
                <Input 
                  placeholder="Auto-filled from address (editable)" 
                allowClear 
                className="input-field"
                size="large"
                style={{ border: '1px solid rgba(254, 2, 125, 0.3)' }}
                  disabled={editingEvent?.isViewMode === true}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={<span style={{ 
                color: 'var(--text-white)', 
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 'var(--spacing-xs)'
                }}>Longitude</span>}
                name="long_location"
            >
              <Input 
                  placeholder="Auto-filled from address (editable)" 
                allowClear 
                className="input-field"
                size="large"
                  disabled={editingEvent?.isViewMode === true}
              />
            </Form.Item>
          </Col> */}
          </Row>

          {/* Modal Footer */}
          <div
            style={{
              textAlign: "center",
              marginTop: "var(--spacing-2xl)",
              paddingTop: "var(--spacing-lg)",
              borderTop: "1px solid rgba(254, 2, 125, 0.2)",
              display: "flex",
              gap: "var(--spacing-md)",
              justifyContent: "center",
            }}
          >
            <Button
              onClick={handleCancel}
              style={{
                minWidth: "120px",
                height: "40px",
                background: "var(--dark-bg-hover)",
                borderColor: "rgba(254, 2, 125, 0.3)",
                color: "var(--text-white)",
              }}
            >
              {editingEvent && !editingEvent.id ? "Close" : "Cancel"}
            </Button>
            <Button
              type="primary"
              onClick={form.submit}
              className="btn-primary"
              loading={loading}
              style={{
                minWidth: "120px",
                height: "40px",
              }}
            >
              {editingEvent?.id ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );

  return (
    <>
      <style>{`
        .event-form .ant-picker {
          border: 1px solid rgba(254, 2, 125, 0.3) !important;
        }
        .event-form .ant-picker:hover {
          border-color: rgba(254, 2, 125, 0.5) !important;
        }
        .event-form .ant-picker-focused {
          border-color: rgba(254, 2, 125, 0.6) !important;
          box-shadow: 0 0 0 2px rgba(254, 2, 125, 0.1) !important;
        }
       
        .event-form textarea.ant-input {
          border: 1px solid rgba(254, 2, 125, 0.3) !important;
        }
        .event-form textarea.ant-input:hover {
          border-color: rgba(254, 2, 125, 0.5) !important;
        }
        .event-form textarea.ant-input:focus {
          border-color: rgba(254, 2, 125, 0.6) !important;
          box-shadow: 0 0 0 2px rgba(254, 2, 125, 0.1) !important;
        }
      `}</style>
      <div
        className={`page-container ${
          sidebarCollapsed ? "sidebar-collapsed" : ""
        }`}
      >
        {/* Header Section */}
        <div className="module-header">
          <h1 className="module-title">Event Management</h1>
          <p className="module-subtitle">
            Manage and monitor all platform events
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stats-card">
            <div className="stats-value">
              {dataSource.meta?.total || dataSource.total || 0}
            </div>
            <div className="stats-label">Total Events</div>
            <CalendarOutlined
              style={{
                color: "var(--primary-purple)",
                fontSize: "1.5rem",
                marginTop: "var(--spacing-sm)",
              }}
            />
          </div>
          <div className="stats-card">
            <div className="stats-value">
              {(dataSource.data || []).filter((e) => {
                const eventDate = dayjs(e.date);
                return eventDate.isAfter(dayjs());
              }).length || 0}
            </div>
            <div className="stats-label">Upcoming Events</div>
            <CalendarOutlined
              style={{
                color: "var(--accent-purple)",
                fontSize: "1.5rem",
                marginTop: "var(--spacing-sm)",
              }}
            />
          </div>
          <div className="stats-card">
            <div className="stats-value">
              {(dataSource.data || []).filter((e) => e.is_full).length || 0}
            </div>
            <div className="stats-label">Full Events</div>
            <CalendarOutlined
              style={{
                color: "var(--error-red)",
                fontSize: "1.5rem",
                marginTop: "var(--spacing-sm)",
              }}
            />
          </div>
          {/* <div className="stats-card">
            <div className="stats-value">
              {(dataSource.data || []).filter((e) => e.link).length || 0}
            </div>
            <div className="stats-label">Events with Links</div>
            <LinkOutlined
              style={{
                color: "var(--success-green)",
                fontSize: "1.5rem",
                marginTop: "var(--spacing-sm)",
              }}
            />
          </div> */}
        </div>

        {/* Filters and Search */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-item">
              <Input
                placeholder="Search events by name..."
                prefix={
                  <SearchOutlined style={{ color: "var(--text-gray-light)" }} />
                }
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={(e) => {
                  setSearchValue(e.target.value);
                  handleSearch({ name: e.target.value });
                }}
                className="input-field"
                allowClear
              />
            </div>
            <div className="filter-item">
              <Select
                value={sortOrder}
                onChange={setSortOrder}
                className="input-field"
                style={{ minWidth: 160 }}
                options={[
                  { value: "date_asc", label: "Date (earliest first)" },
                  { value: "date_desc", label: "Date (latest first)" },
                ]}
              />
            </div>
            <div className="filter-item">
              <Select
                value={statusFilter || "all"}
                onChange={(value) => setStatusFilter(value === "all" ? "" : value)}
                className="input-field"
                style={{ minWidth: 140 }}
                options={[
                  { value: "all", label: "All statuses" },
                  { value: "open", label: "Open" },
                  { value: "full", label: "Full" },
                ]}
              />
            </div>
            <div className="filter-actions">
              <Button
                type="primary"
                className="btn-primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingEvent(null);
                  setAddress("");
                  lastGeocodedAddress.current = "";
                  setShowModal(true);
                  form.resetFields();
                }}
              >
                Create Event
              </Button>
            </div>
          </div>
        </div>

        {/* Events Table */}
        <div
          className={`data-table-card ${
            sidebarCollapsed ? "sidebar-collapsed" : ""
          }`}
        >
          <Table
            columns={columns}
            dataSource={dataSource.data || []}
            className="data-table"
            rowKey="id"
            pagination={{
              onChange: handleChangePage,
              current:
              dataSource.meta?.current_page || dataSource.current_page || 1,
              pageSize: dataSource.meta?.per_page || dataSource.per_page || PAGE_LIMIT,
              total: dataSource.meta?.total || dataSource.total || 0,
              className: "data-table-pagination",
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} events`,
              size: "small",
            }}
            scroll={{ x: tableWidth }}
            size="small"
            bordered={false}
          />
        </div>

        {/* Delete Confirmation Modal */}
        <Modal
          className="modal-overlay"
          width={calculateModalWidth("400px")}
          open={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          footer={null}
          centered
        >
          <div
            style={{
              padding: "var(--spacing-2xl)",
              background: "var(--dark-bg-card)",
              borderRadius: "var(--radius-lg)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                marginBottom: "var(--spacing-xl)",
                fontSize: "3rem",
                color: "var(--error-red)",
              }}
            >
              <DeleteOutlined />
            </div>
            <Title
              level={4}
              style={{
                color: "var(--text-white)",
                marginBottom: "var(--spacing-md)",
                fontFamily: "Poppins-Bold",
              }}
            >
              Confirm Delete
            </Title>
            <p
              style={{
                color: "var(--text-gray)",
                marginBottom: "var(--spacing-xl)",
                fontSize: "0.95rem",
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to delete this event? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-md)",
                justifyContent: "center",
              }}
            >
              <Button
                onClick={() => setDeleteModalVisible(false)}
                style={{
                  minWidth: "120px",
                  height: "40px",
                  background: "var(--dark-bg-hover)",
                  borderColor: "rgba(254, 2, 125, 0.3)",
                  color: "var(--text-white)",
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                onClick={handleConfirmDelete}
                style={{
                  minWidth: "120px",
                  height: "40px",
                  background: "var(--error-red)",
                  borderColor: "var(--error-red)",
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {renderModal}
      </div>
    </>
  );
};

export default Events;
